export const EMPRESA_ETIQUETA_CONFIG_PATH = '/empresas/etiqueta/config';
export const EMPRESA_ETIQUETA_HASH_PARAM = 'etiqueta';

export type EmpresaEtiquetaConfigItem = {
  id: number;
  rfc?: string | null;
  razonSocial?: string | null;
  etiquetaIds?: number[] | null;
};

export type EtiquetaChip = {
  id: number;
  nombre: string;
  color?: string | null;
  habilitado?: boolean | null;
};

export function filterEtiquetaChips(
  etiquetas: EtiquetaChip[],
  query: string,
  selectedIds: number[] = [],
): EtiquetaChip[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [];
  }
  const selected = new Set(selectedIds);
  return etiquetas.filter(item =>
    item.habilitado !== false
    && !selected.has(item.id)
    && (item.nombre ?? '').toLowerCase().includes(q),
  );
}

export function filterEmpresasByQuery(
  empresas: EmpresaEtiquetaConfigItem[],
  query: string,
): EmpresaEtiquetaConfigItem[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return empresas;
  }
  return empresas.filter(empresa => {
    const rfc = (empresa.rfc ?? '').toLowerCase();
    const razonSocial = (empresa.razonSocial ?? '').toLowerCase();
    return rfc.includes(q) || razonSocial.includes(q);
  });
}

export function empresaHasSelectedEtiquetas(
  empresa: EmpresaEtiquetaConfigItem,
  selectedIds: number[],
): boolean {
  if (selectedIds.length === 0) {
    return false;
  }
  const ids = new Set(empresa.etiquetaIds ?? []);
  return selectedIds.every(id => ids.has(id));
}

export function partitionEmpresasByEtiquetas(
  empresas: EmpresaEtiquetaConfigItem[],
  selectedIds: number[],
): { conEtiqueta: EmpresaEtiquetaConfigItem[]; sinEtiqueta: EmpresaEtiquetaConfigItem[] } {
  const conEtiqueta: EmpresaEtiquetaConfigItem[] = [];
  const sinEtiqueta: EmpresaEtiquetaConfigItem[] = [];
  empresas.forEach(empresa => {
    if (empresaHasSelectedEtiquetas(empresa, selectedIds)) {
      conEtiqueta.push(empresa);
    } else {
      sinEtiqueta.push(empresa);
    }
  });
  return { conEtiqueta, sinEtiqueta };
}

export function addEtiquetaChip(selectedIds: number[], etiquetaId: number): number[] {
  if (selectedIds.includes(etiquetaId)) {
    return selectedIds;
  }
  return [...selectedIds, etiquetaId];
}

export function removeEtiquetaChip(selectedIds: number[], etiquetaId: number): number[] {
  return selectedIds.filter(id => id !== etiquetaId);
}

export type EmpresaEtiquetaPane = 'con' | 'sin';

export function etiquetaIdsToAssign(
  empresa: EmpresaEtiquetaConfigItem,
  selectedIds: number[],
  target: EmpresaEtiquetaPane,
): number[] {
  const current = new Set(empresa.etiquetaIds ?? []);
  if (target === 'con') {
    return selectedIds.filter(id => !current.has(id));
  }
  return selectedIds.filter(id => current.has(id));
}

export function nextEtiquetaIds(
  empresa: EmpresaEtiquetaConfigItem,
  selectedIds: number[],
  target: EmpresaEtiquetaPane,
): number[] {
  const current = empresa.etiquetaIds ?? [];
  if (target === 'con') {
    const next = [...current];
    selectedIds.forEach(id => {
      if (!next.includes(id)) {
        next.push(id);
      }
    });
    return next;
  }
  const remove = new Set(selectedIds);
  return current.filter(id => !remove.has(id));
}

export function applyEmpresaEtiquetaMove(
  empresas: EmpresaEtiquetaConfigItem[],
  empresaId: number,
  selectedIds: number[],
  target: EmpresaEtiquetaPane,
): EmpresaEtiquetaConfigItem[] {
  return applyEmpresaEtiquetaMoveMany(empresas, [empresaId], selectedIds, target);
}

export function applyEmpresaEtiquetaMoveMany(
  empresas: EmpresaEtiquetaConfigItem[],
  empresaIds: number[],
  selectedIds: number[],
  target: EmpresaEtiquetaPane,
): EmpresaEtiquetaConfigItem[] {
  if (selectedIds.length === 0 || empresaIds.length === 0) {
    return empresas;
  }
  const ids = new Set(empresaIds);
  return empresas.map(empresa => {
    if (!ids.has(empresa.id)) {
      return empresa;
    }
    return { ...empresa, etiquetaIds: nextEtiquetaIds(empresa, selectedIds, target) };
  });
}

export function canMoveEmpresaEtiqueta(
  from: EmpresaEtiquetaPane,
  to: EmpresaEtiquetaPane,
  selectedIds: number[],
): boolean {
  return from !== to && selectedIds.length > 0;
}

export function parseEtiquetaIdsFromSearch(search: URLSearchParams | string): number[] {
  const params = typeof search === 'string'
    ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    : search;
  const ids: number[] = [];
  params.getAll(EMPRESA_ETIQUETA_HASH_PARAM).forEach(value => {
    String(value)
      .split(',')
      .forEach(part => {
        const id = Number(part.trim());
        if (Number.isInteger(id) && id > 0 && !ids.includes(id)) {
          ids.push(id);
        }
      });
  });
  return ids;
}

export function etiquetaSearchFromIds(ids: number[]): URLSearchParams {
  const params = new URLSearchParams();
  ids.forEach(id => {
    if (Number.isInteger(id) && id > 0) {
      params.append(EMPRESA_ETIQUETA_HASH_PARAM, String(id));
    }
  });
  return params;
}
